import { useMemo, useCallback, useState } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js'

import { COLOR_MODE, COLOR_MODE_REGISTRY } from '../../../../shared/constants/colorMode.js'
import {
  buildColorModeLegendItems,
  encodeColorModeLegendHoverValue,
  getColorModeFilterActive,
  getNextColorModeFiltersConfig,
  isColorModeLegendKeyActive,
} from '../../../catalog/utils/colorModeLegend.js'

import { Button } from '../../../../shared/ui/Button/Button.jsx'
import { FiltersAlert } from '../../../../shared/ui/icons/FiltersAlert.jsx'
import { Select }   from '../../../../shared/ui/Select/index.js'

import styles from './TiersLegend.module.scss'

/**
 * Color legend for the tier floor-plan view.
 *
 * Layout:  label  [ Select color mode ]  swatch-items…  [active-product indicator]
 *
 * Fully data-driven via `COLOR_MODE_REGISTRY` — adding a new color mode
 * requires only a registry entry + CSS rules; zero changes here.
 *
 * Each legend mode maps to a catalog filter (via `filterId` + `toFilterValue`
 * in the registry). Clicking a swatch sets that filter to a single value;
 * clicking again restores the default.  Cross-mode filters stack:
 * rooms=3 + status=available = only available 3-room units.
 *
 * Interactions:
 * - Hover on a legend item → parent strengthens matching SVG paths (via onLegendHover)
 * - Hover on SVG apartment → legend highlights the matching swatch (via hoveredProduct)
 * - Click on a legend item → sets/restores a catalog filter
 *
 * @param {object}   props
 * @param {object}   props.activeFiltersConfig  - current filter state from useCatalog
 * @param {string|null} props.activeProduct     - uuid of the currently open product
 * @param {string}   props.colorMode            - 'rooms' | 'status' | …
 * @param {object}   props.defaultFiltersConfig - CMS default filter state
 * @param {function} props.onColorModeChange    - (mode: string) => void
 * @param {function} props.onLegendHover        - (value: string|number|null) => void
 * @param {object[]} props.products             - all displayable products (NOT filtered)
 * @param {function} props.setActiveFiltersConfig - filter state setter
 */
export function TiersLegend({
  activeFiltersConfig,
  activeFiltersCount = 0,
  activeProduct,
  availableColorModes,
  colorMode,
  defaultFiltersConfig,
  onColorModeChange,
  onLegendHover,
  tierLegendContainerRef,
  products,
  onShowFilters,
  setActiveFiltersConfig,
  showSoldHighlightNote = false,
}) {
  const { t, plural } = useI18n()
  const getProductsCategoryString = useProductStrings()


  // Local hover state: which legend item is the mouse over right now?
  const [localHoveredKey, setLocalHoveredKey] = useState(null)


  const modeConfig = COLOR_MODE_REGISTRY[colorMode] ?? null
  const { filterId, toFilterValue, matchesFilterValue } = modeConfig ?? {}

// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util?------------------------------------------------------------------------------------------------ 
  // ── Build Select options from available registered modes ───────────────
  const modeOptions = useMemo(
    () => (availableColorModes || Object.keys(COLOR_MODE_REGISTRY))
      .map((value) => {
        const cfg = COLOR_MODE_REGISTRY[value];
        if (!cfg) return null;
        return {
          value,
          cfg,
          label: t(cfg.labelKey),
        };
      })
      .filter(Boolean),
    [t, availableColorModes],
  );
// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util? -------------------------------------------------------------------------------------------------
  // ── Build legend items from the registry ────────────────────────────────
  const legendItems = useMemo(() => {
    return buildColorModeLegendItems(modeConfig, products)
  }, [modeConfig, products])
  
// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util? -------------------------------------------------------------------------------------------------
  // ── Filter state for the current mode ───────────────────────────────────
  const modeFilterActive = getColorModeFilterActive(filterId, activeFiltersConfig, defaultFiltersConfig)

  // ── Click handler: default → [x,x] → default ───────────────────────────
  const handleClick = useCallback((legendKey) => {
    if (!filterId || !toFilterValue || !setActiveFiltersConfig || !defaultFiltersConfig) return

    setActiveFiltersConfig((prev) => getNextColorModeFiltersConfig({
      activeFiltersConfig: prev,
      defaultFiltersConfig,
      filterId,
      toFilterValue,
      legendKey,
    }))
  }, [filterId, toFilterValue, setActiveFiltersConfig, defaultFiltersConfig])

  if (!legendItems.length && !activeProduct) return null

// TODO: same logic TiersLegend/LayerLegendPanel — unify? hook? util? -------------------------------------------------------------------------------------------------
  function getLegendLabels(item) {
    const { key, labelKey, labelFn } = item

    if (colorMode === COLOR_MODE.ROOMS) {
      return {
        displayLabel: key === 0 ? t('tiers.rooms_service') : String(key),
        fullLabel: labelFn ? labelFn(t, plural, key) : String(key),
      }
    }

    if (colorMode === COLOR_MODE.INSOLATION) {
      return {
        displayLabel: key === 'null' ? '∅' : key === 10 ? '10h' : `${key}h+`,
        fullLabel: labelKey ? t(labelKey) : String(key),
      }
    }

    if (colorMode === COLOR_MODE.COMPARE) {
      const decile = Number(String(key).replace('p', '')) + 1
      return {
        displayLabel: `${decile * 10}%`,
        fullLabel: t(`product.compare_tooltip_${key}`, `${decile * 10}%`),
      }
    }

    if (colorMode === COLOR_MODE.CATEGORY) {
      const fullLabel = getProductsCategoryString(null, key, { count: 1 })
      return {
        displayLabel: fullLabel,
        fullLabel,
      }
    }

    const fullLabel = labelFn ? labelFn(t, plural, key) : t(labelKey)
    return {
      displayLabel: fullLabel,
      fullLabel,
    }
  }

  const showFilterAlert = activeFiltersCount > 0 && typeof onShowFilters === 'function'

  function isLegendKeyActive(key) {
    return isColorModeLegendKeyActive({
      legendKey: key,
      filterId,
      toFilterValue,
      matchesFilterValue,
      activeFiltersConfig,
      defaultFiltersConfig,
    })
  }

  return (
    <div 
    ref={tierLegendContainerRef}
    className={styles.legendHolder}>
      <div className={styles.labelPane}>
        <div className={styles.labelContainer}>
          <span className={styles.legendLabel}>{t('tiers.legend_label')}</span>
          <Select
            value={colorMode}
            onChange={onColorModeChange}
            options={modeOptions}
            className={styles.modeSelect}
            preferDropUp
          />
        </div>
        {showFilterAlert && (
          <Button
            type="button"
            size='medium'
            className={styles.filterAlertButton}
            onClick={onShowFilters}
            aria-label={t('common.filter')}
            title={t('common.filter')}
          >
            <FiltersAlert className={styles.cornerIcon} />
            <span className={styles.filterAlertMark} aria-hidden="true">!</span>
          </Button>
        )}
        {showSoldHighlightNote && (
          <div className={styles.soldBadgeBox}>
          <span className={styles.soldBadge}>
            {t('status.sold')}
          </span>
          </div>
        )}
      </div>

      <div className={styles.items}>
      {/* <div className={styles.itemsGrid}> */}
        {legendItems.map(({ key, labelKey, labelFn, fill, stroke, hoverFill, hoverOpacity }) => {
          const isActive = isLegendKeyActive(key)
          const isOff     = modeFilterActive && !isActive
          const isHighlighted = localHoveredKey === key
          const label     = labelFn ? labelFn(t, plural, key) : t(labelKey)
          const { displayLabel, fullLabel } = getLegendLabels({ key, labelKey, labelFn })

          // Swatch fill: off → transparent, highlighted → hoverFill, default → fill
          const swatchBg = isOff ? 'transparent' : (isHighlighted && hoverFill ? hoverFill : fill)

          return (
            <button
              key={key}
              data-legend-key={encodeColorModeLegendHoverValue(colorMode, key)}
              type="button"
              className={[
                styles.item,
                isOff     ? styles.itemOff     : '',
                isActive  ? styles.itemActive  : '',
                isHighlighted ? styles.itemHighlight : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleClick(key)}
              onMouseEnter={() => {
                onLegendHover(encodeColorModeLegendHoverValue(colorMode, key))
                setLocalHoveredKey(key)
              }}
              onMouseLeave={() => {
                onLegendHover(null)
                setLocalHoveredKey(null)
              }}
              onFocus={() => {
                onLegendHover(encodeColorModeLegendHoverValue(colorMode, key))
                setLocalHoveredKey(key)
              }}
              onBlur={() => {
                onLegendHover(null)
                setLocalHoveredKey(null)
              }}
              title={fullLabel}
            >
              <span
                className={styles.swatch}
                style={{
                  backgroundColor: swatchBg,
                  borderColor: stroke,
                  opacity: isHighlighted && hoverOpacity ? hoverOpacity : undefined,
                }}
              />
              <span className={styles.itemLabel}>{displayLabel}</span>
              {/* {label} */}
            </button>
          )
        })}

        {/* Active (open) product indicator */}
        {activeProduct && (
          <span className={`${styles.item} ${styles.itemActiveProduct}`}>
            <span className={`${styles.swatch} ${styles.swatchActive}`} />
            <span className={styles.itemLabel}>
              {t('tiers.active_product')}
            </span>
          </span>
        )}
      </div>
    </div>
  )
}
